// src/firebase.js
// Firebase helpers: Crashlytics, Analytics, FCM (foundation), Remote Config (foundation).
//
// Routing:
//   Native (Capacitor Android) — @capacitor-firebase/* plugins (v8.1.0)
//   Web / PWA                  — firebase JS SDK (Analytics only)
//                                Crashlytics is native-only; web errors go to Sentry.
//
// All functions are fire-and-forget async — errors are silently swallowed so a
// Firebase failure never blocks the UI.
//
// Usage:
//   import { setAnalyticsUserId, logEvent, recordException } from './firebase';

// ─── Detect Capacitor native context ─────────────────────────────────────────
// Mirrors the same check used in useHealthConnect.js and WorkoutApp.jsx.
function detectNative() {
  if (typeof window === 'undefined') return false;
  return (
    window.location.protocol === 'capacitor:' ||
    !!(window.Capacitor?.isNativePlatform?.())
  );
}

// =============================================================================
// Crashlytics
// =============================================================================
// Records a caught JS error into Firebase Crashlytics (native only).
// On web, this is a silent no-op — Sentry handles web error capture.
// Unhandled errors are forwarded via the window.error / unhandledrejection
// handlers wired in src/index.js.
export async function recordException(error) {
  if (!detectNative()) return;
  try {
    const { FirebaseCrashlytics } = await import('@capacitor-firebase/crashlytics');
    const message = (error instanceof Error ? error.message : String(error)) || 'Unknown error';
    await FirebaseCrashlytics.recordException({ message });
  } catch (_) {}
}

// =============================================================================
// Analytics
// =============================================================================
// Logs a named event with optional parameters.
//   Native: @capacitor-firebase/analytics → FirebaseAnalytics.logEvent()
//   Web:    firebase JS SDK  → logEvent()  (requires initializeApp in index.js)
export async function logEvent(name, params = {}) {
  try {
    if (detectNative()) {
      const { FirebaseAnalytics } = await import('@capacitor-firebase/analytics');
      await FirebaseAnalytics.logEvent({ name, params });
    } else {
      const { getApps, getApp } = await import('firebase/app');
      if (!getApps().length) return; // Firebase not initialised (dev mode)
      const { getAnalytics, logEvent: fbLogEvent } = await import('firebase/analytics');
      fbLogEvent(getAnalytics(getApp()), name, params);
    }
  } catch (_) {}
}

// Sets the Analytics user ID — call once after a successful profile load.
//   Native: @capacitor-firebase/analytics → FirebaseAnalytics.setUserId()
//   Web:    firebase JS SDK  → setUserId()
export async function setAnalyticsUserId(userId) {
  try {
    if (detectNative()) {
      const { FirebaseAnalytics } = await import('@capacitor-firebase/analytics');
      await FirebaseAnalytics.setUserId({ userId });
    } else {
      const { getApps, getApp } = await import('firebase/app');
      if (!getApps().length) return;
      const { getAnalytics, setUserId: fbSetUserId } = await import('firebase/analytics');
      fbSetUserId(getAnalytics(getApp()), userId);
    }
  } catch (_) {}
}

// =============================================================================
// FCM — foundation only
// =============================================================================
// Requests notification permission, retrieves the FCM registration token, and
// logs it in development. Token persistence (Supabase) and foreground message
// handling are deferred to a future sprint.
// Export only — not called anywhere yet.
export async function initFCM() {
  if (!detectNative()) return;
  try {
    const { FirebaseMessaging } = await import('@capacitor-firebase/messaging');
    await FirebaseMessaging.requestPermissions();
    const { token } = await FirebaseMessaging.getToken();
    if (process.env.NODE_ENV !== 'production') {
      console.log('[firebase] FCM token:', token);
    }
    // TODO: persist token to Supabase (push_tokens table) for server-triggered pushes
  } catch (_) {}
}

// =============================================================================
// Remote Config — foundation only
// =============================================================================
// Fetches and activates the latest Remote Config values from Firebase.
// No feature flags are wired to Remote Config yet — deferred to a future sprint.
// Export only — not called anywhere yet.
export async function initRemoteConfig() {
  if (!detectNative()) return;
  try {
    const { FirebaseRemoteConfig } = await import('@capacitor-firebase/remote-config');
    await FirebaseRemoteConfig.fetchAndActivate();
  } catch (_) {}
}
